import { useParams } from '@tanstack/react-router'
// eslint-disable-next-line boundaries/element-types
import { Navbar } from 'features/navbar'
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react'
import styled from 'styled-components'

const PageMaxWidthContext = createContext<
  | {
      maxWidth: string
      setMaxWidth: Dispatch<SetStateAction<string>>
    }
  | undefined
>(undefined)

const usePageMaxWidth = () => {
  const context = useContext(PageMaxWidthContext)
  if (!context) {
    throw new Error('usePageMaxWidth must be used within a CantileverPage.')
  }
  return context
}

const AppContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100vh; /* Ensure the layout takes full viewport height */
  overflow: hidden; /* Prevent overflow of content beyond the viewport */
`

const PageBg = styled.div<{ $enableScrolling: boolean }>`
  overflow-y: ${({ $enableScrolling }) =>
    $enableScrolling ? 'auto' : 'hidden'}; /* Enable scrolling */
  background-color: ${({ theme }) => theme.forecast.pageBackground1};
  position: relative;
  width: 100%;
`

const ContentContainer = styled.div<{ $maxWidth?: string }>`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  margin: 0 auto;
  max-width: ${({ $maxWidth }) => $maxWidth};
`

export const MaxWidthEnforcer = styled.div<{ $maxWidth: string }>`
  width: 100%;
  max-width: ${({ $maxWidth }) => $maxWidth};
  margin-left: auto;
  margin-right: auto;
`

export const CantileverPage: React.FC<{
  enableScrolling?: boolean
  enforceMaxWidth?: boolean
  showSidebar?: boolean
  children: ReactNode
}> = ({ enableScrolling, enforceMaxWidth, showSidebar = true, children }) => {
  const { siteID } = useParams({ strict: false })
  const [maxWidth, setMaxWidth] = useState<string>('64rem')

  return (
    <AppContainer>
      {showSidebar && <Navbar siteID={siteID} />}
      <PageMaxWidthContext.Provider value={{ maxWidth, setMaxWidth }}>
        <PageBg $enableScrolling={enableScrolling ?? false}>
          <ContentContainer $maxWidth={enforceMaxWidth ? maxWidth : 'none'}>
            {children}
          </ContentContainer>
        </PageBg>
      </PageMaxWidthContext.Provider>
    </AppContainer>
  )
}

// Export the hook for use within children of CantileverPage
export { usePageMaxWidth }
